// store/slices/users.ts — the current paginated Users table view.
//
// Holds ONLY the page currently on screen. The full users table is the
// "streaming cursor exception" from CLAUDE.md — we never load the whole
// dataset into Redux. Filters/sort/page live in this slice so the Users page
// is a pure read from state; the rpc-driven effect dispatches on every
// change.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AdminUser, AdminUserSort } from "../types";

export interface UsersQuery {
  q: string;
  sort: AdminUserSort;
  order: "asc" | "desc";
  page: number;
  pageSize: number;
}

export interface UsersState {
  query: UsersQuery;
  currentPage: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  /** id of the latest in-flight request — used to drop stale acks. */
  lastRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  query: {
    q: "",
    sort: "created_at",
    order: "desc",
    page: 1,
    pageSize: 25,
  },
  currentPage: [],
  total: 0,
  page: 1,
  pageSize: 25,
  lastRequestId: null,
  loading: false,
  error: null,
};

const users = createSlice({
  name: "users",
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<Partial<UsersQuery>>) {
      state.query = { ...state.query, ...action.payload };
    },
    requestStarted(
      state,
      action: PayloadAction<{ requestId: string }>,
    ) {
      state.lastRequestId = action.payload.requestId;
      state.loading = true;
      state.error = null;
    },
    requestSucceeded(
      state,
      action: PayloadAction<{
        requestId: string;
        users: AdminUser[];
        total: number;
        page: number;
        pageSize: number;
      }>,
    ) {
      // Drop stale acks — only the most recent request's result wins.
      if (state.lastRequestId !== action.payload.requestId) return;
      state.currentPage = action.payload.users;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.loading = false;
      state.error = null;
    },
    requestFailed(
      state,
      action: PayloadAction<{ requestId: string; error: string }>,
    ) {
      if (state.lastRequestId !== action.payload.requestId) return;
      state.loading = false;
      state.error = action.payload.error;
    },
    /** Patch a single row in the current page (used after flag toggles). */
    patchRow(state, action: PayloadAction<AdminUser>) {
      const idx = state.currentPage.findIndex(
        (u) => u.id === action.payload.id,
      );
      if (idx >= 0) {
        state.currentPage[idx] = action.payload;
      }
    },
    clear() {
      return initialState;
    },
  },
});

export default users;
